import { useMemo, useCallback, useEffect } from 'react';
import type { ChatWidgetProps, ChatWidgetSettings } from '../types';
import { useEndpointConfig } from './useEndpointConfig';
import { useWidgetBehavior } from './useWidgetBehavior';
import {
  isHiddenBubbleOption,
  isHiddenSuggestedAction,
  normalizeActionValue,
} from '../utils/actionFilters';
import { NEUTRAL_MERCHANT_PRESET } from '../merchants/neutral';
import { getCurrentHostname } from '../merchants/hostname';
import { useMerchantPreset } from '../merchants/useMerchantPreset';
import { normalizeWidgetConfig } from '../config/normalize';
import { useShallowStable } from '../config/stable';
import { validateWidgetProps } from '../config/validate';

function mergeSettings(
  base?: ChatWidgetSettings | null,
  override?: ChatWidgetSettings | null
): ChatWidgetSettings {
  if (!base && !override) return {};
  if (!base) return override || {};
  if (!override) return base;
  const result: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    const baseValue = (base as Record<string, unknown>)[key];
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      baseValue &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue)
    ) {
      result[key] = mergeSettings(baseValue as ChatWidgetSettings, value as ChatWidgetSettings);
    } else {
      result[key] = value;
    }
  });
  return result as ChatWidgetSettings;
}

/**
 * Resolves the legacy prop surface into the values the widget renders from.
 * All precedence and defaults live in `normalizeWidgetConfig` (see
 * `src/config/`); this hook only adds merchant-preset fallbacks, filtering and
 * stable identities. The return shape is consumed by `ChatWidget`.
 */
export function useResolvedProps(
  props: ChatWidgetProps,
  remoteSettings: ChatWidgetSettings | null
) {
  // Raw merged settings (remote < inline) are still exposed for ad-hoc `readSetting` lookups.
  const settings = useMemo(
    () => mergeSettings(remoteSettings, props.settings),
    [remoteSettings, props.settings]
  );

  const readSetting = useCallback(
    (path: string) =>
      path
        .split('.')
        .reduce(
          (acc: Record<string, unknown>, key) =>
            (acc && (acc as Record<string, unknown>)[key] !== undefined
              ? (acc as Record<string, unknown>)[key]
              : undefined) as Record<string, unknown>,
          settings as Record<string, unknown>
        ),
    [settings]
  );

  const config = useMemo(
    () => normalizeWidgetConfig(props, remoteSettings),
    [props, remoteSettings]
  );

  // Development-only diagnostics: one console.warn (and one console.info for the
  // legacy analytics inference) per mount. Stripped from production builds.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
      const { warnings, deprecated } = validateWidgetProps(props);
      if (warnings.length) {
        const message = `[ChatWidget] configuration:\n- ${warnings.join('\n- ')}`;
        if (deprecated.length) {
          // The v2 migration helper is dev tooling: loaded lazily here so it never reaches the
          // production bundle (this whole branch is dead code there).
          import(/* webpackChunkName: "config-migrate" */ '../config/migrate')
            .then(({ formatMigrationSnippet }) => formatMigrationSnippet(props))
            .catch(() => null)
            .then(snippet => console.warn(snippet ? `${message}\n\n${snippet}` : message));
        } else {
          console.warn(message);
        }
      }
      if (config.analytics.enabledSource === 'inferred') {
        // eslint-disable-next-line no-console
        console.info(
          '[ChatWidget] analytics enabled because analyticsBaseUrl is set and analyticsEnabled is not; set analyticsEnabled (or settings.analytics.enabled) explicitly.'
        );
      }
    }
    // Once per mount by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merchant preset (explicit prop/setting wins, else hostname match, else neutral)
  const { merchantId, merchantPreset, merchantPresetLoaded } = useMerchantPreset(
    config.merchant.preset,
    getCurrentHostname()
  );

  const { branding, i18n, agent, sessions, features, security, endpoints, behavior } = config;

  const theme = useShallowStable(branding.theme);
  const translations = useShallowStable(i18n.translations);

  const suggestedActionsSource = branding.suggestedActions;
  const filteredSuggestedActions = useMemo(() => {
    const source =
      suggestedActionsSource ??
      merchantPreset.suggestedActions ??
      NEUTRAL_MERCHANT_PRESET.suggestedActions ??
      [];
    const items = Array.isArray(source) ? source : [];
    return items
      .map(item => String(item || '').trim())
      .filter(item => item.length > 0)
      .filter(item => {
        const normalized = normalizeActionValue(item);
        return !isHiddenSuggestedAction(normalized);
      });
  }, [suggestedActionsSource, merchantPreset]);

  const bubbleOptions = branding.bubbleOptions;
  const filteredBubbleOptions = useMemo(() => {
    if (!Array.isArray(bubbleOptions)) return [];
    return bubbleOptions.filter(option => {
      const label = typeof option === 'string' ? option : option?.label;
      const value = typeof option === 'string' ? option : (option?.value ?? option?.label);
      const labelNorm = normalizeActionValue(label);
      const valueNorm = normalizeActionValue(value);
      return !(isHiddenBubbleOption(labelNorm) || isHiddenBubbleOption(valueNorm));
    });
  }, [bubbleOptions]);

  // Delegate to sub-hooks
  const endpointValues = useEndpointConfig({ config });
  const behaviorValues = useWidgetBehavior({
    props,
    config,
    sessionHistoryEnabled: sessions.enabled,
  });

  return {
    settings,
    readSetting,
    merchantId,
    merchantPreset,
    merchantPresetLoaded,
    companyName: branding.companyName,
    companyLogo: branding.companyLogo,
    agentName: branding.agentName,
    agentAvatar: branding.agentAvatar,
    theme,
    darkMode: branding.darkMode,
    welcomeMessage: branding.welcomeMessage,
    suggestedActions: filteredSuggestedActions,
    localizeActions:
      suggestedActionsSource == null &&
      (merchantPreset === NEUTRAL_MERCHANT_PRESET || merchantPreset.suggestedActions == null),
    bubbleOptions,
    placeholder: branding.placeholder,
    language: i18n.language,
    textDirection: i18n.textDirection,
    translations,
    customerId: sessions.customerId,
    settingsConsoleUrl: branding.settingsConsoleUrl,
    settingsConsoleLabel: branding.settingsConsoleLabel,
    customCss: security.customCss,
    cspNonce: security.cspNonce,
    agentOrgId: agent.orgId,
    agentId: agent.agentId,
    kbCollection: agent.kbCollection,
    kbApiKey: agent.kbApiKey,
    kbTopK: agent.kbTopK,
    kbScoreThreshold: agent.kbScoreThreshold,
    conversionPrompt: agent.conversionPrompt,
    sessionHistoryEnabled: sessions.enabled,
    maxSessions: sessions.maxSessions,
    allowAttachments: features.allowAttachments,
    allowVoice: features.allowVoice,
    allowVideo: features.allowVideo,
    apiKey: security.apiKey,
    apiBaseUrl: endpoints.apiBaseUrl,
    provider: agent.provider,
    enableStreaming: features.enableStreaming,
    enableToolVisualization: features.enableToolVisualization,
    enableRichMessages: features.enableRichMessages,
    enableSourceCitations: features.enableSourceCitations,
    enableThinking: features.enableThinking,
    defaultOpen: behavior.defaultOpen,
    filteredBubbleOptions,
    ...endpointValues,
    ...behaviorValues,
  };
}
